import type { RouterPage } from '@wix/editor-platform-sdk-types'
import type {
  DynamicPageRouterData,
  UnifiedPageRef,
} from '@wix/wix-data-client-common-standalone'
import { keys } from '@wix/wix-data-client-common-standalone'
import { find, findKey, first, get, omit } from 'lodash'
import type { PatternDefinition, RouterConfig } from './routerConfigTypes'
import { generatePageTitle } from './templates'

export const findPageIdByPageRole = (
  router: DynamicPageRouterData,
  pageRole: string,
): string | undefined => {
  const page = router.pages.find(
    ({ pageRoles }) => first(pageRoles) === pageRole,
  )
  return page?.pageRef.id
}

const getPageRole = (
  routerPages: RouterPage[],
  pageRef: UnifiedPageRef,
): string => {
  const routerPage = find(routerPages, page => page.pageRef.id === pageRef.id)!
  return get(routerPage, 'pageRoles[0]')
}

export const getPatternDefForPage = (
  routerConfig: RouterConfig,
  routerPages: RouterPage[],
  pageRef: UnifiedPageRef,
): { pattern: string; def: PatternDefinition } => {
  const pageRole = getPageRole(routerPages, pageRef)
  const pattern = findKey(routerConfig.patterns, {
    pageRole,
  }) as string

  return {
    pattern,
    def: routerConfig.patterns[pattern],
  }
}

export const deletePattern = (
  routerConfig: RouterConfig,
  pattern: string,
): RouterConfig => ({
  ...omit(routerConfig, 'patterns'),
  patterns: omit(routerConfig.patterns, pattern),
})

export const addPattern = (
  routerConfig: RouterConfig,
  pattern: string,
  patternDefinition: PatternDefinition,
): RouterConfig => ({
  ...routerConfig,

  patterns: {
    ...routerConfig.patterns,
    [pattern]: patternDefinition,
  },
})

export const updatePagePattern = (
  routerConfig: RouterConfig,
  oldPattern: string,
  newPattern: string,
): RouterConfig => {
  const oldPatternDef = routerConfig.patterns[oldPattern]
  const newPateTitle = generatePageTitle(
    oldPatternDef.config.collection,
    newPattern,
  )
  const newPatternDef = {
    ...oldPatternDef,
    title: newPateTitle,
  }
  return addPattern(
    deletePattern(routerConfig, oldPattern),
    newPattern,
    newPatternDef,
  )
}

export const updatePatternDef = (
  routerConfig: RouterConfig,
  pattern: string,
  newPatternDef: PatternDefinition,
): RouterConfig => ({
  ...routerConfig,

  patterns: {
    ...routerConfig.patterns,
    [pattern]: newPatternDef,
  },
})

export const getAllPatterns = (routerConfig: RouterConfig): string[] =>
  keys(routerConfig.patterns) || []

export const getPatternDef = (
  routerConfig: RouterConfig,
  pattern: string,
): PatternDefinition => routerConfig.patterns[pattern]
